import { supabase } from '../lib/supabase';

/**
 * Normalizes Supabase errors into user-friendly messages
 */
const normalizeError = (error) => {
  if (!error) return 'An unexpected error occurred';

  const errorMessage = error.message || error.toString();

  // Supabase error codes and messages
  if (errorMessage.includes('Invalid login credentials') || 
      errorMessage.includes('Invalid credentials') ||
      errorMessage.includes('Email not confirmed')) {
    return 'Invalid email or password';
  }

  if (errorMessage.includes('User already registered') || 
      errorMessage.includes('already registered')) {
    return 'This email is already registered';
  }

  if (errorMessage.includes('Email not confirmed') || 
      errorMessage.includes('email not confirmed')) {
    return 'Please check your email to confirm your account';
  }

  if (errorMessage.includes('Password should be at least') ||
      errorMessage.includes('Password is too short')) {
    return 'Password must be at least 8 characters';
  }

  if (errorMessage.includes('Invalid email')) {
    return 'Please enter a valid email address';
  }

  // Log raw error in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Supabase auth error:', error);
  }

  // Default user-friendly message
  return 'An error occurred. Please try again.';
};

/**
 * Sign up a new user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} nickname - User nickname (optional)
 * @returns {Promise<{success: boolean, error?: string, needsConfirmation?: boolean, session?: object}>}
 */
export const signUp = async (email, password, nickname = null) => {
  try {
    const signUpData = {
      email,
      password,
    };

    // Add nickname to user metadata if provided
    if (nickname) {
      signUpData.options = {
        data: {
          nickname: nickname.trim(),
        },
      };
    }

    const { data, error } = await supabase.auth.signUp(signUpData);

    if (error) {
      // Log full error in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Supabase signUp error:', error);
      }
      return {
        success: false,
        error: normalizeError(error),
      };
    }

    // Check if email confirmation is required
    // If user is null, it means email confirmation is required
    // If user exists, auto-login occurred
    const needsConfirmation = !data.user || !data.session;

    return {
      success: true,
      needsConfirmation,
      session: data.session,
      user: data.user,
    };
  } catch (error) {
    return {
      success: false,
      error: normalizeError(error),
    };
  }
};

/**
 * Sign in an existing user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<{success: boolean, error?: string, session?: object}>}
 */
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      // Log full error in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Supabase signIn error:', error);
      }
      return {
        success: false,
        error: normalizeError(error),
      };
    }

    return {
      success: true,
      session: data.session,
      user: data.user,
    };
  } catch (error) {
    return {
      success: false,
      error: normalizeError(error),
    };
  }
};

/**
 * Sign out the current user
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return {
        success: false,
        error: normalizeError(error),
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      error: normalizeError(error),
    };
  }
};

/**
 * Get the current session
 * @returns {Promise<object|null>} Current session or null
 */
export const getSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error getting session:', error);
      }
      return null;
    }

    return data.session;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error getting session:', error);
    }
    return null;
  }
};

/**
 * Subscribe to authentication state changes
 * @param {Function} callback - Callback function that receives (event, session)
 * @returns {object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  const { data } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });
  return data;
};

